import { type ReactNode, createContext, useRef, useContext } from "react";
import { type StoreApi, useStore } from "zustand";
import { createLauncherStore, type LauncherStore } from "./launcherStore";

export const LauncherStoreContext = createContext<StoreApi<LauncherStore> | undefined>(undefined);

export interface LauncherStoreProviderProps {
  children: ReactNode;
}

/**
 * The store contains all global state that was previously passed down from LauncherContext
 * This context passes down a Zustand store to reduce rerenders in consumer components
 */
export const LauncherStoreProvider = ({ children }: LauncherStoreProviderProps) => {
  const storeRef = useRef<StoreApi<LauncherStore> | null>(null);

  if (!storeRef.current) {
    storeRef.current = createLauncherStore();
  }

  return (
    <LauncherStoreContext.Provider value={storeRef.current}>
      {children}
    </LauncherStoreContext.Provider>
  );
};

export const useLauncherStore = <T,>(selector: (store: LauncherStore) => T): T => {
  const launcherStoreContext = useContext(LauncherStoreContext);

  if (!launcherStoreContext) {
    throw new Error(`useLauncherStore must be use within LauncherStoreProvider`);
  }

  return useStore(launcherStoreContext, selector);
};
